import { DateTime, Duration } from 'luxon';
import React, { useEffect, useState, useRef } from 'react';

import { Text, Flex } from '@vimeo/bokeh';
import { Calendar } from '@vimeo/bokeh-icons';
import { translate } from '@vimeo/i18n-runtime';

const SECONDS_IN_MINUTE = 60;
const SECONDS_IN_HOUR = SECONDS_IN_MINUTE * 60;

const getTimeLeftString = (secondsUntilLive?: number) => {
  if (!secondsUntilLive) {
    return '';
  }

  const duration = Duration.fromObject({ seconds: secondsUntilLive });

  // If there's more than an hour left, show HRS MINS or else show MINS SECS.
  if (secondsUntilLive > 60 * SECONDS_IN_MINUTE) {
    return duration.toFormat(" hh 'hours' mm 'minutes'");
  }
  return secondsUntilLive > 120
    ? duration.toFormat(" m 'minutes'")
    : duration.toFormat(" m 'minute'");
};

type Props = {
  /** `2025-03-28T13:43:29.716-07:00` - The date time stamp in string format as it comes back from the API */
  startTime: string;
  /** `white` The color of the text displayed in the component */
  textColor: string;
};

/**
 * StartingTimestamp component.
 *
 * This component is used to display the scheduled start timestamp of a showcase.
 * - If the showcase is within 12 hours, it displays a countdown timer.
 * - If the showcase starts within 1 minute, it displays "Starting soon...".
 * - If the showcase is more than 12 hours away, it displays a static timestamp in the format:
 *   "MMMM d, yyyy 'at' h:mm a ZZZZ".
 */
export const StartingTimestamp = ({ textColor, startTime }: Props) => {
  const countdownInterval = useRef<number | null>(null);
  const [secondsUntilLive, setSecondsUntilLive] = useState<number>(0);
  const shouldCountdownStop = secondsUntilLive <= 0;
  const [showStaticTimeStamp, setShowStaticTimestamp] = useState(false);

  useEffect(() => {
    const nowDateTime = DateTime.fromJSDate(new Date());
    const startDateTime = DateTime.fromISO(startTime);

    // startTime should match today's date if it starts today
    const startingTodaySingle =
      nowDateTime.toFormat('LL/dd/yyyy') === startDateTime.toFormat('LL/dd/yyyy');

    /**
     * Use this diff to only show the countdown if under 12 hours until start time.
     * Also, do not show countdown if past time (diff > 0 seconds)
     */
    const diff = startDateTime.diff(nowDateTime, ['hours', 'seconds', 'days']);
    const isInFuture = diff.seconds > 0;
    const isInNext12Hours = diff.hours < 12 && isInFuture;

    const showCountdown = isInNext12Hours && startingTodaySingle;
    const showStaticDateTime = isInFuture && (diff.hours >= 12 || diff.days > 0);
    if (showCountdown) {
      setSecondsUntilLive(SECONDS_IN_HOUR * diff.hours + diff.seconds);
    }
    if (showStaticDateTime) setShowStaticTimestamp(true);
  }, [startTime]);

  useEffect(() => {
    const clearCountdown = () => {
      if (countdownInterval.current !== null) {
        clearInterval(countdownInterval.current);
        countdownInterval.current = null; // Reset to null after clearing
      }
    };

    if (shouldCountdownStop && countdownInterval.current !== null) {
      clearCountdown();
      return; // Exit early to avoid setting a new interval
    }

    const updateCountdown = () => {
      setSecondsUntilLive((prevSecondsUntilLive) => {
        if (prevSecondsUntilLive <= 0) {
          clearCountdown(); // Clear the countdown when it hits 0 or below
          return 0; // Ensure the state doesn't go below 0
        }
        return prevSecondsUntilLive - 1;
      });
    };

    countdownInterval.current = setInterval(updateCountdown, 1000);

    return () => {
      clearCountdown();
    };
  }, [shouldCountdownStop]);

  let content: string | null = null;

  if (secondsUntilLive) {
    content =
      secondsUntilLive > SECONDS_IN_MINUTE
        ? translate({
            singular: 'Starts in {TIME}',
            replacements: {
              TIME: getTimeLeftString(secondsUntilLive),
            },
          })
        : translate('Starting soon...');
  } else if (showStaticTimeStamp) {
    content = DateTime.fromISO(startTime).toFormat("MMMM d, yyyy 'at' h:mm a ZZZZ");
  }

  if (!content) return null;

  return (
    <Flex justifyContent="start" alignItems="center" gap="sm">
      <Calendar w={24} h={24} color={textColor} />
      <Text variant={{ xs: 'heading-xs', md: 'heading-sm' }} color={textColor}>
        {content}
      </Text>
    </Flex>
  );
};
